import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useScheduleBoardModals } from 'af-root/hooks/useScheduleBoardModal';
import AssignmentsModalSection from 'af-root/scenes/Company/ScheduleBoard/Shared/AssignmentsModalSection';
import Loading from 'af-root/scenes/Company/ScheduleBoard/Shared/LoadingModal';

import type ScheduleBoardViewModel from 'ab-viewModels/scheduleBoard.viewModel';

import type CreateTemporaryEmployeeAssignmentRM from 'ab-requestModels/workOrderTemporaryEmployee/createTemporaryEmployeeAssignment';

import type { ScheduleBoardWorkOrderResourceLookupsViewModel } from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardResourceLookup.viewModel';
import type ScheduleBoardWorkOrderViewModel from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { formatPhoneNumber } from '@acceligentllc/shared/utils/phone';

import { CountryCode } from '@acceligentllc/shared/enums/countryCode';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import { MAX_EMPLOYEE_ASSIGNMENTS_PER_DAY } from 'ab-common/constants/scheduleBoard';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import LabelWithColor from 'af-components/LabelWithColor';
import LockedValue from 'af-components/LockedValue';

interface OwnProps {
	createAssignment: (workOrderId: number, data: CreateTemporaryEmployeeAssignmentRM) => void;
	hasPermissionsToEditScheduleBoard: boolean;
	removeAssignment: (workOrderId: number, workOrderResourceLookupId: number) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

type StateFromProps = {
	assignedWorkOrders: ScheduleBoardWorkOrderViewModel[];
	assignableWorkOrders: ScheduleBoardWorkOrderViewModel[];
	lookups: ScheduleBoardWorkOrderResourceLookupsViewModel;
	workOrders: { [workOrderCode: string]: ScheduleBoardWorkOrderViewModel; };
};

const _getStateFromProps = (
	id: Nullable<number>,
	date: Nullable<string>,
	workOrdersByDateDictionary: ScheduleBoardViewModel['workOrdersByDateDictionary']
): StateFromProps => {
	if (!id || !date || !workOrdersByDateDictionary) {
		return {
			assignedWorkOrders: [],
			assignableWorkOrders: [],
			lookups: [],
			workOrders: {},
		};
	}

	const workOrders = workOrdersByDateDictionary[date];

	const assignedWorkOrderCodes = workOrders?.temporaryEmployeeAssignments?.[id] ?? [];
	const assignedWorkOrders: ScheduleBoardWorkOrderViewModel[] = assignedWorkOrderCodes
		.map((workOrderCode) => workOrders?.workOrders?.[workOrderCode])
		.filter((_wo): _wo is ScheduleBoardWorkOrderViewModel => (!!_wo));

	return {
		assignedWorkOrders,
		assignableWorkOrders: Object.keys(workOrders?.workOrders ?? {})
			.reduce((_acc, _woCode) => {
				if (
					workOrders?.workOrders?.[_woCode]
					&& !assignedWorkOrderCodes.includes(_woCode)
					&& workOrders.workOrders[_woCode].status !== WorkOrderStatus.CANCELED
				) {
					_acc.push(workOrders.workOrders[_woCode]);
				}
				return _acc;
			}, [] as ScheduleBoardWorkOrderViewModel[]),
		lookups: workOrders?.workOrderResourceLookups,
		workOrders: workOrders?.workOrders,
	};
};

const TemporaryEmployeeModal: React.FC<Props> = (props: Props) => {
	const {
		createAssignment,
		hasPermissionsToEditScheduleBoard,
		removeAssignment,
		workOrdersByDateDictionary,
		isAllowedToManagePerDiems,
	} = props;

	const { temporaryEmployee, temporaryEmployeeModalDate, setTemporaryEmployeeModalData } = useScheduleBoardModals();

	const {
		assignedWorkOrders,
		assignableWorkOrders,
		lookups,
		workOrders,
	} = React.useMemo(
		() => _getStateFromProps(temporaryEmployee?.id ?? null, temporaryEmployeeModalDate, workOrdersByDateDictionary),
		[workOrdersByDateDictionary, temporaryEmployee, temporaryEmployeeModalDate]
	);

	const assignedInOffices = React.useMemo(() => assignedWorkOrders.filter((wo) => !!wo?.officeNickname), [assignedWorkOrders]);

	const temporaryEmployeeLookupIds = React.useMemo(() => {
		return Object.values(lookups).filter((_lookup) =>
			_lookup.temporaryEmployeeId === temporaryEmployee?.id
		).map((_lookup) => _lookup.id);
	}, [lookups, temporaryEmployee?.id]);

	const workOrderLookups = React.useMemo(() => {
		return Object.values(workOrders).reduce((_acc: { [workOrderId: number]: number; }, _wo: ScheduleBoardWorkOrderViewModel) => {
			const _workOrderResourceLookups = _wo.workOrderResourceLookups;

			const _lookupIntersect = _workOrderResourceLookups.filter((_lookup) => new Set(temporaryEmployeeLookupIds).has(_lookup));
			if (_lookupIntersect.length) {
				_acc[_wo.id] = _lookupIntersect[0];
			}
			return _acc;
		}, {});
	}, [temporaryEmployeeLookupIds, workOrders]);

	const closeModal = React.useCallback(() => {
		setTemporaryEmployeeModalData(null, null);
	}, [setTemporaryEmployeeModalData]);

	const onCreateAssignment = React.useCallback(async (workOrderId: number) => {
		if (!hasPermissionsToEditScheduleBoard || !temporaryEmployee) {
			return false;
		}
		createAssignment(workOrderId, { temporaryEmployeeId: temporaryEmployee.id });
		return true;
	}, [hasPermissionsToEditScheduleBoard, temporaryEmployee, createAssignment]);

	const onRemoveAssignment = React.useCallback((workOrderId: number, workOrderCode: string, itemId: number, resourceLookupId?: number) => {
		if (!hasPermissionsToEditScheduleBoard || !resourceLookupId) {
			return;
		}
		removeAssignment(workOrderId, resourceLookupId);
	}, [hasPermissionsToEditScheduleBoard, removeAssignment]);

	const renderLoadingModalContent = React.useCallback(() => {
		return (<Loading onHide={closeModal} />);
	}, [closeModal]);

	const renderDataModalContent = React.useCallback(() => {
		if (!temporaryEmployee) {
			throw new Error('Employee not loaded');
		}

		const {
			email,
			firstName,
			id,
			jobCode,
			lastName,
			phoneNumber,
			agency,
			agencyColor,
		} = temporaryEmployee;

		return (
			<>
				<CustomModal.Header className="schedule-board-modal__resource__header">
					<div>
						<span className="icon-temp_labor schedule-board-modal__resource__header__icon"></span>
						<span>{firstName} {lastName}</span>
					</div>
					<LabelWithColor color={agencyColor} text={agency} />
				</CustomModal.Header>
				<CustomModal.Body>
					<div className="schedule-board-modal__resource__row">
						<div className="m-b-m">
							<SegmentLabel
								label="Agency"
								tooltip="The agency this laborer or equipment is associated with."
							/>
						</div>
						<Row className="row--non-padded">
							<Col md={6}>
								<LockedValue
									label="Name"
									value={<LabelWithColor color={agencyColor} text={agency} />}
								/>
							</Col>
						</Row>
					</div>
					<hr />
					<div className="schedule-board-modal__resource__row">
						<div className="m-b-m m-t-m">
							<SegmentLabel
								label="Contact Information"
								tooltip="Contact details for reaching this laborer, including mobile phone and email."
							/>
						</div>
						<Row className="row--non-padded">
							<Col md={6}>
								<LockedValue
									label="Mobile Phone"
									value={formatPhoneNumber(phoneNumber, CountryCode.US)}
								/>
							</Col>
							<Col md={6}>
								<LockedValue
									label="Email"
									value={email}
								/>
							</Col>
							<Col md={12} />
						</Row>
					</div>
					<hr />
					<div className="schedule-board-modal__resource__row">
						<div className="m-b-m m-t-m">
							<SegmentLabel
								label="Current Status"
								tooltip="The job and location this laborer or equipment is currently assigned to."
							/>
						</div>
						<Row className="row--non-padded">
							<Col md={6}>
								<LockedValue
									label="Job"
									value={jobCode}
								/>
							</Col>
							<Col md={6}>
								<LockedValue
									label="Job Locations"
									value={assignedInOffices.length > 0
										? assignedInOffices.map((wo) => wo?.officeNickname).join(', ')
										: '-'
									}
								/>
							</Col>
						</Row>
					</div>
					<hr />
					<AssignmentsModalSection
						assignableWorkOrders={assignableWorkOrders}
						assignedWorkOrders={assignedWorkOrders}
						createAssignment={onCreateAssignment}
						isAllowedToManagePerDiems={isAllowedToManagePerDiems}
						isDeletable={hasPermissionsToEditScheduleBoard}
						isEditable={hasPermissionsToEditScheduleBoard}
						itemId={id}
						maxItems={MAX_EMPLOYEE_ASSIGNMENTS_PER_DAY}
						removeAssignment={onRemoveAssignment}
						resourceLookupIds={workOrderLookups}
						showPerDiem={false}
					/>
				</CustomModal.Body>
				<CustomModal.Footer>
					<Button
						label="Close"
						onClick={closeModal}
						style="secondary"
					/>
				</CustomModal.Footer>
			</>
		);
	}, [
		assignableWorkOrders,
		assignedInOffices,
		assignedWorkOrders,
		closeModal,
		hasPermissionsToEditScheduleBoard,
		isAllowedToManagePerDiems,
		onCreateAssignment,
		onRemoveAssignment,
		temporaryEmployee,
		workOrderLookups,
	]);

	return (
		<CustomModal
			className="schedule-board-modal__resource"
			closeModal={closeModal}
			modalStyle="info"
			showModal={!!temporaryEmployee}
			size="xl"
		>
			{temporaryEmployee ? renderDataModalContent() : renderLoadingModalContent()}
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData, userData } = state.user;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const isAllowedToManagePerDiems: boolean = isAllowed(PagePermissions.COMPANY.PER_DIEMS, companyData.permissions, companyData.isCompanyAdmin, userData.role);

	return {
		isAllowedToManagePerDiems,
		workOrdersByDateDictionary: state.scheduleBoard.workOrdersByDateDictionary,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	connector,
	React.memo
);

export default enhance(TemporaryEmployeeModal);
